import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import type {
    DeliveryType,
    ErrorResponseType,
    OrdersStatisticsMetric,
    OrdersStatisticsQueryParams,
    OrdersStatisticsResponse,
    OrdersStatus,
    StatisticsGranularity,
} from "@src/types";
import { getOrdersStatisticsApi } from "@src/api";
import type { AxiosError } from "axios";

export const ORDERS_STATISTICS_DEFAULT_METRICS: OrdersStatisticsMetric[] = [
    "count",
    "total_price",
];

export interface UseOrdersStatisticsParams {
    startDate: string;
    endDate: string;
    granularity: StatisticsGranularity;
    metrics?: OrdersStatisticsMetric[];
    deliveryType?: DeliveryType | null;
    preOrder?: boolean | null;
    status?: OrdersStatus | null;
    merchantId?: string | null;
    enabled?: boolean;
}

export function useOrdersStatistics({
    startDate,
    endDate,
    granularity,
    metrics,
    deliveryType,
    preOrder,
    status,
    merchantId,
    enabled = true,
}: UseOrdersStatisticsParams) {
    const params = useMemo<OrdersStatisticsQueryParams>(() => {
        const metricsList =
            metrics && metrics.length > 0 ? metrics : ORDERS_STATISTICS_DEFAULT_METRICS;
        return {
            start_date: startDate,
            end_date: endDate,
            granularity,
            metrics: metricsList,
            delivery_type: deliveryType ?? undefined,
            pre_order: preOrder ?? undefined,
            status: status ?? undefined,
            merchant_id: merchantId ?? undefined,
        };
    }, [deliveryType, endDate, granularity, merchantId, metrics, preOrder, startDate, status]);

    const canFetch =
        enabled && Boolean(params.start_date && params.end_date && params.metrics.length > 0);

    return useQuery<OrdersStatisticsResponse, AxiosError<ErrorResponseType>>({
        queryKey: ["orders-statistics", params],
        queryFn: ({ signal }) => getOrdersStatisticsApi(params, signal),
        enabled: canFetch,
        refetchOnWindowFocus: false,
    });
}
